import { Controller, useFormContext } from 'react-hook-form';
import { Button, Datepicker, PasswordInut } from 'shared-ui';
import { useEffect, useState } from 'react';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { Input } from 'ui/input/Input';
import { Form } from 'ui/form/Form';
import { useAppSelectOptions } from 'hooks/useAppSelectOptions/useAppSelectOptions';
import * as Styled from '../UserDetails.styles';
import { UserStatusEnum } from 'api/users/users.enum';
import { getEndOfToday } from 'helpers/getEndOfToday';
import { DateFormat } from 'core/global.enum';
import { UpdatePasswordContainer } from 'context/updatePasswordModal/updatePasswordModalContextController/updatePassword/UpdatePasswordContainer';
import { useAuth } from 'hooks/useAuth/useAuth';

import { UserBasicDetailsProps, UserBasicDetailsForm } from './UserBasicDetails.types';

export const UserBasicDetails = ({ isLoadingUpdateUser, selectedUser, onSubmit }: UserBasicDetailsProps) => {
  const { formatMessage } = useLocale();
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const isSupervisorUser = !selectedUser.ktoWprowadzil;
  const { user } = useAuth();

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty, isValid, errors },
    watch,
    setValue,
    clearErrors,
  } = useFormContext<UserBasicDetailsForm>();

  const isUserInactive = watch('status') === UserStatusEnum.NIEAKTYWNY;
  const isCurrentUser = selectedUser.id === user?.id;

  const { userStatusOptions } = useAppSelectOptions();

  useEffect(() => {
    setValue('dataDo', null);
    clearErrors('dataDo');
  }, [isUserInactive]);

  return (
    <Styled.Form onSubmit={handleSubmit(onSubmit)}>
      <Styled.FieldsWrapper>
        <Controller
          name="username"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              disabled={true}
              value={field.value || ''}
              placeholder={formatMessage({ id: AppMessages['user.details.basic.username.placeholder'] })}
              label={formatMessage({ id: AppMessages['user.details.basic.username'] })}
              errorMessage={errors?.username?.message}
            />
          )}
        />

        <Styled.FieldWrapper>
          <Form.ControlledSelect
            name="status"
            control={control}
            disabled={isSupervisorUser || isCurrentUser}
            label={formatMessage({ id: AppMessages['user.details.basic.status'] })}
            options={userStatusOptions}
          />
        </Styled.FieldWrapper>

        <Controller
          name="dataDo"
          control={control}
          render={({ field }) => (
            <Datepicker
              {...field}
              disabled={isUserInactive || isSupervisorUser || isCurrentUser}
              label={formatMessage({ id: AppMessages['users.addUser.field.date.label'] })}
              placeholderText={formatMessage({ id: AppMessages['users.addUser.field.date.placeholder'] })}
              minDate={new Date()}
              withCalendarIcon
              withDatepickerPopover
              isClickable
              errorMessage={errors?.dataDo?.message}
              popoverOrigin={{
                vertical: -80,
                horizontal: 60,
              }}
              customDateFormatOnOpen={getEndOfToday}
              withoutTimeInput
              mediumPlaceholderFontSize
              inputFormat={DateFormat.DATE}
            />
          )}
        />

        <PasswordInut onModalOpen={() => setIsChangePasswordModalOpen(true)} />
        <UpdatePasswordContainer
          user={selectedUser}
          isModalOpen={isChangePasswordModalOpen}
          setIsModalOpen={setIsChangePasswordModalOpen}
        />
      </Styled.FieldsWrapper>

      {isDirty && (
        <Styled.ButtonsWrapper>
          <Button
            size="small"
            onClick={() => {
              reset(selectedUser);
            }}
            color="primary"
          >
            {formatMessage({ id: AppMessages['owner.details.button.cancel'] })}
          </Button>
          <Button
            type="submit"
            disabled={!isValid}
            isLoading={isLoadingUpdateUser}
            size="small"
            variant="contained"
            color="primary"
          >
            {formatMessage({ id: AppMessages['owner.details.button.save'] })}
          </Button>
        </Styled.ButtonsWrapper>
      )}
    </Styled.Form>
  );
};
